import { describeFunctions } from '../../utils/formula';
import { argumentAt, measureOfArgument, paramFor } from './formulaArguments';
import { formulaLiteralSpans } from './tokenTemplate';

/**
 * The suggestions a formula editor offers while someone types (KPR-332): the
 * pure half. Nothing here touches the DOM or Lexical, so every rule is a plain
 * test. `useFormulaSuggestions` feeds it the editor's text and caret;
 * `FormulaSuggestions` draws what it answers.
 *
 * THE SEAM. A suggestion source is a PROVIDER:
 *
 *   {
 *     id,
 *     match(text, caret, env)  -> { start, end, query } | null
 *                                 the span of `text` the caret is typing, or
 *                                 null when this provider has nothing to say
 *                                 there;
 *     items(query, env, match) -> { items, more }
 *                                 the rows to offer for that span, best first;
 *                                 `more` is how many it left out;
 *     accept(item, match, text) -> an edit: { start, end, text?, token?,
 *                                 caretBack? }  — replace [start, end) with the
 *                                 text, or with a pill for `token`, or with
 *                                 `parts?` in order (each { text } | { token }
 *                                 | { choice: { text, label } }, a choice being
 *                                 a value pill, KPR-350); park the caret
 *                                 `caretBack` characters before the end of
 *                                 what was inserted; `reopen` asks for the
 *                                 list again at the new caret (the next
 *                                 argument's picker),
 *   }
 *
 * and an item is { key, label, detail?, description?, example? }. `env` is
 * `{ tokens }`, the same vocabulary the editor's `{{ }}` button lists, so a
 * provider can never offer an element the person could not insert by hand.
 *
 * Three providers ship — an element after `{{`, the argument the caret is in
 * (KPR-350: `describeFunction(name).params` says an element, a unit or a date
 * part goes there, and a picked value is a pill), and a function's name. The
 * first provider whose match has rows wins, so a more specific provider goes
 * before a more general one. A match is handed back to `items` with the
 * formula `text` on it. An item may carry `group`; the popup then draws the
 * rows under group headings, in the order given.
 *
 * The function list is read from `describeFunctions()` on every call: a
 * function added to the formula module is suggested with no change here.
 */

/** The most element rows offered at once; the rest are reached by typing more. */
export const MAX_REFERENCE_ROWS = 50;

const IDENTIFIER_AT_END = /[A-Za-z_][A-Za-z0-9_]*$/;
const WORD_CHAR = /[A-Za-z0-9_]/;
const OPEN_REFERENCE = /\{\{([^{}\n]*)$/;

const inSpan = (spans, at) => spans.some(([start, end]) => at >= start && at < end);

// ---------------------------------------------------------- function names

/**
 * The function a person is typing: the identifier that ends at the caret.
 * Free typing stays free, so every doubt is a "no":
 *   - inside a text literal, or inside an unfinished `{{ … `;
 *   - glued to a digit, a letter or a dot in front (`2rou`, `a.rou`);
 *   - a unit written after a number (`5 kg`, `5 USD` — not a function);
 *   - the caret in the middle of a word (`ro|und`).
 */
function matchFunction(text, caret) {
	if (typeof text !== 'string' || caret < 1 || caret > text.length) return null;
	const before = text.slice(0, caret);
	const found = IDENTIFIER_AT_END.exec(before);
	if (!found) return null;
	const start = caret - found[0].length;
	if (start > 0 && /[A-Za-z0-9_.]/.test(text[start - 1])) return null;
	if (caret < text.length && WORD_CHAR.test(text[caret])) return null;
	if (OPEN_REFERENCE.test(before.slice(0, start))) return null;
	if (inSpan(formulaLiteralSpans(text), start)) return null;
	const earlier = before.slice(0, start).replace(/\s+$/, '');
	if (/[0-9]$/.test(earlier)) return null;
	return { start, end: caret, query: found[0], followedByParen: text[caret] === '(' };
}

function rankFunctions(query) {
	const q = query.toLowerCase();
	const startsWith = [];
	const contains = [];
	describeFunctions().forEach(fn => {
		const name = fn.name.toLowerCase();
		if (name.startsWith(q)) startsWith.push(fn);
		else if (name.includes(q)) contains.push(fn);
	});
	return startsWith.concat(contains);
}

/** `round(number, decimals?)` — the signature a row prints (the picker's own label). */
export const functionSignature = fn => `${fn.name}(${fn.args.join(', ')})`;

export const functionProvider = {
	id: 'function',
	match: matchFunction,
	items(query) {
		const items = rankFunctions(query).map(fn => ({
			key: `function:${fn.name}`,
			kind: 'function',
			name: fn.name,
			label: functionSignature(fn),
			detail: fn.group,
			description: fn.description,
			example: fn.example,
			takesArguments: fn.args.length > 0,
		}));
		return { items, more: 0 };
	},
	accept(item, match) {
		// A `(` already follows the word: write the name and leave the rest.
		if (match.followedByParen) return { start: match.start, end: match.end, text: item.name, caretBack: 0 };
		// Inside the new parens the first argument's picker opens, if it has one.
		return { start: match.start, end: match.end, text: `${item.name}()`, caretBack: item.takesArguments ? 1 : 0, ...(item.takesArguments ? { reopen: true } : {}) };
	},
};

// ---------------------------------------------------------------- elements

/** Every insertable element of a token tree: its token, its words, and the groups it sits under. */
function referenceEntries(tokens) {
	const out = [];
	const seen = new Set();
	const walk = (nodes, trail) => (Array.isArray(nodes) ? nodes : []).forEach(node => {
		if (!node) return;
		const label = node.label || node.menuLabel || node.token || '';
		if (node.token && !node.disabled && !seen.has(node.token)) {
			seen.add(node.token);
			// The pill's words often already name the group (`Vehicle · Weight`):
			// a hint that repeats them says nothing.
			const lower = label.toLowerCase();
			out.push({ token: node.token, label, trail: trail.filter(group => !lower.includes(group.toLowerCase())) });
		}
		if (Array.isArray(node.children)) walk(node.children, label ? trail.concat(label) : trail);
	});
	walk(tokens, []);
	return out;
}

/**
 * An element: what follows an unclosed `{{`. Not inside a text literal (there
 * `{{` is text), and the caret is at the end of what was typed so far.
 */
function matchReference(text, caret) {
	if (typeof text !== 'string' || caret < 2 || caret > text.length) return null;
	const found = OPEN_REFERENCE.exec(text.slice(0, caret));
	if (!found) return null;
	const start = caret - found[0].length;
	if (inSpan(formulaLiteralSpans(text), start)) return null;
	return { start, end: caret, query: found[1].trim() };
}

export const referenceProvider = {
	id: 'reference',
	match: matchReference,
	items(query, env) {
		const q = String(query || '').toLowerCase();
		const entries = referenceEntries(env && env.tokens);
		const inner = token => token.slice(2, -2).trim().toLowerCase();
		const starts = [];
		const rest = [];
		entries.forEach(entry => {
			const label = entry.label.toLowerCase();
			if (!q) starts.push(entry);
			else if (label.startsWith(q) || inner(entry.token).startsWith(q)) starts.push(entry);
			else if (label.includes(q) || inner(entry.token).includes(q)) rest.push(entry);
		});
		const all = starts.concat(rest);
		const items = all.slice(0, MAX_REFERENCE_ROWS).map(entry => ({
			key: `reference:${entry.token}`,
			kind: 'element',
			token: entry.token,
			label: entry.label,
			detail: entry.trail.join(' › '),
		}));
		return { items, more: Math.max(0, all.length - items.length) };
	},
	accept(item, match) {
		return { start: match.start, end: match.end, token: item.token };
	},
};

// --------------------------------------------------------------- arguments

/** The most value rows (units, date parts) offered at once; the rest are reached by typing more. */
export const MAX_CHOICE_ROWS = 100;

// A value being typed in a unit or a date-part argument without its quote:
// `kilo`, `liters per`, `km/h`, `lb-oz`. Anything else there (an operator, a reference, a
// call) is an expression the person is writing, and free typing stays free.
const BARE_WORD = /^[A-Za-z0-9 ./-]*$/;

/**
 * The argument the caret is in, when the function says what goes there
 * (KPR-350). Only at the END of what is typed in it, and only where a picker
 * helps:
 *   - an `element` argument that is still empty (just after `(` or `, `);
 *   - a `unit` or `choice` argument that is empty, a quote being typed
 *     (`"k`) or a bare word (`kilo`).
 * A `number` or `text` argument offers nothing: it is typed.
 */
function matchArgument(text, caret) {
	const at = argumentAt(text, caret);
	if (!at || !at.atEnd) return null;
	const param = paramFor(at.fn, at.index);
	if (!param) return null;
	const typed = at.typed.trim();
	const base = { start: at.start, end: at.end, at, param };
	if (param.kind === 'element') {
		if (typed || at.inLiteral) return null;
		return { ...base, query: '' };
	}
	if (param.kind !== 'unit' && param.kind !== 'choice') return null;
	if (!Array.isArray(param.choices) || !param.choices.length) return null;
	if (!typed) return { ...base, query: '' };
	const quote = typed[0];
	if (quote === '"' || quote === "'") {
		// A quote still being typed: nothing closed it, nothing escaped.
		if (!at.inLiteral || typed.includes('\\')) return null;
		return { ...base, query: typed.slice(1).trim() };
	}
	if (at.inLiteral || !BARE_WORD.test(typed)) return null;
	return { ...base, query: typed };
}

/** How well a choice answers what was typed: lower is better, null is not at all. */
function choiceScore(choice, q) {
	if (!q) return 0;
	const value = String(choice.value).toLowerCase();
	const label = String(choice.label || '').toLowerCase();
	if (value === q || label === q) return 0;
	if (value.startsWith(q)) return 1;
	if (label.startsWith(q) || label.split(/[\s/-]+/).some(word => word.startsWith(q))) return 2;
	if (value.includes(q) || label.includes(q)) return 3;
	if (String(choice.group || '').toLowerCase().includes(q)) return 4;
	return null;
}

/**
 * The choices for what was typed, kept GROUPED (units under their measure,
 * date parts under Calendar and Clock): the group with the best row first,
 * the best rows first inside it, the registry's order otherwise.
 */
function rankChoices(choices, query) {
	const q = String(query || '').trim().toLowerCase();
	const groups = new Map();
	choices.forEach((choice, index) => {
		const score = choiceScore(choice, q);
		if (score === null) return;
		const key = choice.group || '';
		if (!groups.has(key)) groups.set(key, { order: groups.size, best: score, rows: [] });
		const group = groups.get(key);
		group.best = Math.min(group.best, score);
		group.rows.push({ choice, score, index });
	});
	return [...groups.values()]
		.sort((a, b) => a.best - b.best || a.order - b.order)
		.flatMap(group => group.rows.sort((a, b) => a.score - b.score || a.index - b.index).map(row => row.choice));
}

/** A value as the formula stores it: a double-quoted text, the canonical spelling. */
const quoted = value => `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

export const argumentProvider = {
	id: 'argument',
	match: matchArgument,
	items(query, env, match) {
		const { param, at } = match;
		if (param.kind === 'element') return referenceProvider.items('', env);
		let choices = param.choices;
		// convert(): the unit belongs to the measure of the measurement it
		// converts, when that is one element whose measure is known.
		if (Number.isInteger(param.measureOf) && at.slots[param.measureOf]) {
			const measure = measureOfArgument(match.text, at.slots[param.measureOf], env && env.tokens);
			const narrowed = measure ? choices.filter(choice => choice.measure === measure) : [];
			if (narrowed.length) choices = narrowed;
		}
		const all = rankChoices(choices, query);
		const items = all.slice(0, MAX_CHOICE_ROWS).map(choice => ({
			key: `choice:${choice.value}`,
			kind: 'choice',
			value: choice.value,
			label: choice.label || choice.value,
			// The spelling the formula keeps (`kg`): the one place it shows.
			detail: choice.value,
			group: choice.group || '',
		}));
		return { items, more: Math.max(0, all.length - items.length) };
	},
	/**
	 * The picked value, written in the canonical form: a space after the
	 * argument's comma, a unit or a date part in double quotes. When the
	 * function's next argument is required and none is written yet, `, ` is
	 * added and the list reopens for it (convert: the measurement, then its
	 * unit); the last argument of a call left open gets its `)`. A condition
	 * or a repeating argument gets neither: the person is still writing it.
	 */
	accept(item, match) {
		const { at, param } = match;
		const parts = [];
		if (at.index > 0) parts.push({ text: ' ' });
		parts.push(item.kind === 'element' ? { token: item.token } : { choice: { text: quoted(item.value), label: item.label } });
		const next = paramFor(at.fn, at.index + 1);
		// An argument the person is likely still writing is left open: a
		// condition (`if({{qty}} > 5, …`: the element is rarely the whole test)
		// and a repeating one (`sum(a, b, c)`: only they know how many).
		const condition = Array.isArray(param.accepts) && param.accepts.length === 1 && param.accepts[0] === 'bool';
		const stillWriting = condition || !!param.repeat;
		let caretBack = 0;
		let reopen = false;
		if (stillWriting) {
			// Nothing added: the caret stays after the value.
		} else if (at.next !== ',' && next && !next.optional && !next.repeat) {
			parts.push({ text: ', ' });
			reopen = true;
		} else if (at.next === '') {
			// An open call whose last argument this is: close it, caret before the `)`.
			parts.push({ text: ')' });
			caretBack = 1;
		}
		return { start: at.start, end: at.end, parts, caretBack, reopen };
	},
};

/** In the order they are asked: the narrower trigger first. */
export const PROVIDERS = [referenceProvider, argumentProvider, functionProvider];

/**
 * What to offer at `caret` in `text`, or null when nothing. The first provider
 * that matches AND has rows answers; `contextKey` names the span it is typing,
 * so a caret move inside the same word keeps the same list open.
 */
export function suggestionAt(text, caret, env, providers = PROVIDERS) {
	for (const provider of providers) {
		const found = provider.match(text, caret, env);
		if (!found) continue;
		const match = { ...found, text };
		const { items, more } = provider.items(match.query, env, match);
		if (items.length) return { provider, match, items, more, contextKey: `${provider.id}:${match.start}` };
	}
	return null;
}

/** The edit that accepting `item` makes, from the answer `suggestionAt` gave. */
export function editFor(suggestion, item, text) {
	return suggestion.provider.accept(item, suggestion.match, text);
}
